import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  bulkDeleteSubscriptions,
  createSubscription,
  deleteSubscription,
  getSubscription,
  getSubscriptionCategories,
  listSubscriptions,
  updateSubscription,
  updateSubscriptionCategories,
  type SubscriptionCategoryDef,
  type SubscriptionFilters,
  type SubscriptionInput,
} from '../lib/subscriptionsApi';
import { useActiveCompanyId } from '../store/useCompanyStore';

const SUBSCRIPTIONS_KEY = ['subscriptions'] as const;

export function useSubscriptions(filters: SubscriptionFilters = {}) {
  const activeCompanyId = useActiveCompanyId() ?? undefined;
  const merged: SubscriptionFilters = { companyId: activeCompanyId, ...filters };
  return useQuery({
    queryKey: [...SUBSCRIPTIONS_KEY, merged],
    queryFn: () => listSubscriptions(merged),
    placeholderData: (prev) => prev,
  });
}

export function useSubscription(id: string | undefined) {
  return useQuery({
    queryKey: [...SUBSCRIPTIONS_KEY, 'detail', id],
    queryFn: () => getSubscription(id as string),
    enabled: Boolean(id),
  });
}

// Settings-managed category nomenclature (code + label).
export function useSubscriptionCategories() {
  return useQuery({
    queryKey: [...SUBSCRIPTIONS_KEY, 'categories'],
    queryFn: getSubscriptionCategories,
    staleTime: 5 * 60 * 1000,
  });
}

export function useUpdateSubscriptionCategories() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (categories: SubscriptionCategoryDef[]) => updateSubscriptionCategories(categories),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
    },
  });
}

export function useCreateSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubscriptionInput) => createSubscription(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
    },
  });
}

export function useUpdateSubscription(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SubscriptionInput) => updateSubscription(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
    },
  });
}

export function useDeleteSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteSubscription(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
    },
  });
}

export function useBulkDeleteSubscriptions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => bulkDeleteSubscriptions(ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_KEY });
    },
  });
}
